<template>
  <Modal
    v-model:visible="isVisible"
    class="remove-orders-modal"
    :header="t('common.removeParam', { param: t('orders.order(s)') })"
    size="md"
    @keyup.enter="onRemove"
    @keyup.esc="isVisible = false"
  >
    <p>{{ t('removeOrders.modalText') }}</p>

    <Message v-if="isRemovingAllOrders" severity="error" class="rygen-message">
      {{ t('removeOrders.removingAllOrdersWarning') }}
    </Message>
    <Message
      v-else-if="selectedOrderIds.length"
      severity="error"
      class="rygen-message"
    >
      <p>
        {{
          t('removeOrders.removingTheseOrdersWarning', selectedOrderIds.length)
        }}
      </p>
      <ul>
        <li>{{ t('removeOrders.invalidateRate') }}</li>
        <li>{{ t('removeOrders.changeLineItemStatus') }}</li>
        <!--
          TODO: Calculate which stops are at risk of being removed and show
          them in this warning, by name or city/state
        -->
        <li>{{ t('removeOrders.removeStop') }}</li>
      </ul>
    </Message>

    <ul role="list" class="remove-orders-list">
      <li v-for="order in relatedOrders.data" :key="order.id">
        <RemoveOrdersListItem
          :order="order"
          :selected="!!order.id && selectedOrderIds.includes(order.id)"
          @update:selected="onUpdateSelected(order.id, $event)"
        />
      </li>
    </ul>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-times"
        :disabled="!canRemoveOrders"
        :label="t('common.remove')"
        :severity="Severity.DANGER"
        @click="onRemove"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Loadable, Severity } from '@rygen/rygen-ui/model';

import { Order, Shipment, ShipmentStatus } from '@/model';
import { isStatusEqualOrAfter } from '@/util';

import RemoveOrdersListItem from '@/components/removeOrders/RemoveOrdersListItem.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  relatedOrders: {
    type: Object as PropType<Loadable<Order[]>>,
    default: () => ({ loading: false }),
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
});
const { relatedOrders, shipment } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', orderIds: number[]): void;
}>();

const { t } = useI18n();

const selectedOrderIds: Ref<number[]> = ref([]);

const isRemovingAllOrders = computed(() => {
  return selectedOrderIds.value.length === relatedOrders.value.data?.length;
});

const canRemoveOrders = computed(() => {
  const hasSelection = selectedOrderIds.value.length;

  // User cannot cancel shipment once in transit (i.e. require at least 1 order)
  const isInTransit = isStatusEqualOrAfter(
    shipment.value.status,
    ShipmentStatus.IN_TRANSIT,
  );
  const isRemovingAllInTransit = isRemovingAllOrders.value && isInTransit;

  return hasSelection && !isRemovingAllInTransit;
});

const onUpdateSelected = (orderId: number | undefined, isSelected: boolean) => {
  if (!orderId) {
    return;
  }

  if (isSelected) {
    selectedOrderIds.value.push(orderId);
  } else {
    selectedOrderIds.value = selectedOrderIds.value.filter(
      (id) => id !== orderId,
    );
  }
};

const onRemove = async () => {
  if (canRemoveOrders.value) {
    isVisible.value = false;
    emit('remove', selectedOrderIds.value);
  }
};

watch(isVisible, (newVal) => {
  if (!newVal) {
    selectedOrderIds.value = [];
  }
});
</script>

<style>
.remove-orders-modal {
  p {
    margin-bottom: var(--sp-3);
  }

  .remove-orders-list {
    display: flex;
    flex-flow: column;
    gap: var(--sp-3);
    list-style: none;
    margin: var(--sp-6) 0 0;
    padding: 0;
  }
}
</style>
